import { useEffect, useState } from "react";
import { FileText, PenLine, Mail, CalendarDays, PenTool } from "lucide-react";
import { api, openFile } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const INCOME_LABELS = {
  w2: "W-2 Wages", "1099": "1099 Income", self_employed: "Self-Employment", rental: "Rental Income",
  investment: "Investments / Dividends", retirement: "Retirement / Pension", other: "Other Income",
};

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 text-sm border-b border-border pb-2 last:border-0">
      <span className="text-muted-foreground w-36 shrink-0">{label}</span>
      <span className="break-words whitespace-pre-wrap">{value}</span>
    </div>
  );
}

export function ClientDetailDialog({ client, open, onOpenChange }) {
  const [intake, setIntake] = useState(null);
  const [forms, setForms] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    setLoading(true);
    setIntake(null);
    setForms([]);
    setDocs([]);
    Promise.all([
      api.get(`/intakes/${client.user_id}`).then((r) => setIntake(r.data)).catch(() => {}),
      api.get(`/completed-forms?user_id=${client.user_id}`).then((r) => setForms(r.data)).catch(() => {}),
      api.get(`/documents?user_id=${client.user_id}`).then((r) => setDocs(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [open, client]);

  const p = intake?.personal || {};
  const inc = intake?.income || {};
  const py = intake?.prior_year || {};
  const sig = intake?.signature || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{client?.client_name || client?.name}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> {client?.email}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-6 pt-2">
            <section data-testid="detail-intake-section">
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium mb-3">Intake Information</p>
              {!intake ? (
                <p className="text-sm text-muted-foreground">No intake submitted yet.</p>
              ) : (
                <div className="space-y-4">
                  <div className="border border-border rounded-md p-4 space-y-2">
                    <InfoRow label="Name" value={`${p.firstName || ""} ${p.lastName || ""}`.trim()} />
                    <InfoRow label="Date of birth" value={p.dob} />
                    <InfoRow label="SSN (last 4)" value={p.ssnLast4 ? `••••${p.ssnLast4}` : ""} />
                    <InfoRow label="Phone" value={p.phone} />
                    <InfoRow label="Address" value={[p.street, p.city, p.state, p.zip].filter(Boolean).join(", ")} />
                    <InfoRow label="Filing status" value={p.filingStatus?.replace(/_/g, " ")} />
                  </div>
                  {(intake.dependents || []).length > 0 && (
                    <div className="border border-border rounded-md p-4">
                      <p className="text-sm font-medium mb-2">Dependents</p>
                      {intake.dependents.map((d, i) => (
                        <p key={i} className="text-sm text-muted-foreground" data-testid={`detail-dependent-${i}`}>
                          {d.name} — {d.relationship}{d.dob ? `, born ${d.dob}` : ""}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="border border-border rounded-md p-4 space-y-2">
                    <p className="text-sm font-medium">Income Sources</p>
                    <div className="flex flex-wrap gap-2">
                      {(inc.sources || []).map((s) => (
                        <Badge key={s} variant="secondary" data-testid={`detail-income-${s}`}>{INCOME_LABELS[s] || s}</Badge>
                      ))}
                      {(inc.sources || []).length === 0 && <p className="text-sm text-muted-foreground">None listed</p>}
                    </div>
                    <InfoRow label="Details" value={inc.details} />
                  </div>
                  <div className="border border-border rounded-md p-4 space-y-2">
                    <p className="text-sm font-medium">Prior Year Filing</p>
                    <InfoRow label="Filed last year" value={py.filedLastYear} />
                    <InfoRow label="Result" value={py.result} />
                    <InfoRow label="Prepared by" value={py.preparer} />
                    <InfoRow label="Notes" value={py.notes} />
                  </div>
                  {sig.fullName && (
                    <div className="border border-border rounded-md p-4" data-testid="detail-signature-section">
                      <p className="text-sm font-medium flex items-center gap-1.5 mb-2"><PenTool className="w-4 h-4 text-primary" /> E-Signature</p>
                      <p className="text-sm text-muted-foreground">Signed by <span className="text-foreground font-medium">{sig.fullName}</span> on {sig.signedAt?.slice(0, 10)}</p>
                      {sig.dataUrl && <img src={sig.dataUrl} alt="Client signature" className="mt-2 h-16 border border-border rounded bg-white" data-testid="detail-signature-image" />}
                    </div>
                  )}
                </div>
              )}
            </section>

            <section data-testid="detail-online-forms-section">
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium mb-3">Online Form Submissions</p>
              {forms.length === 0 && <p className="text-sm text-muted-foreground">None submitted online.</p>}
              <div className="space-y-2">
                {forms.map((f) => (
                  <details key={f.entry_id} className="border border-border rounded-md p-3" data-testid={`detail-form-${f.entry_id}`}>
                    <summary className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <PenLine className="w-3.5 h-3.5 text-primary inline" /> {f.form_title} · {f.submitted_at?.slice(0, 10)}
                    </summary>
                    <dl className="mt-3 space-y-1.5">
                      {Object.entries(f.data || {}).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-xs">
                          <dt className="text-muted-foreground capitalize w-40 shrink-0">{k.replace(/_/g, " ")}</dt>
                          <dd className="break-words whitespace-pre-wrap">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                ))}
              </div>
            </section>

            <section data-testid="detail-documents-section">
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium mb-3">Uploaded Documents</p>
              {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents uploaded.</p>}
              <div className="space-y-2">
                {docs.map((d) => (
                  <div key={d.file_id} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`detail-doc-${d.file_id}`}>
                    <FileText className="w-4 h-4 text-primary" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-muted-foreground capitalize flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" /> {(d.form_name || d.category || "").replace(/_/g, " ")} · {d.created_at?.slice(0, 10)}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openFile(d.file_id)} data-testid={`detail-doc-open-${d.file_id}`}>Open</Button>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
